import type { ThunkAction, UnknownAction } from "@reduxjs/toolkit";
import type { RootState } from "../../../store/store";
import { artOfTheDayApi } from "./artOfTheDayApi";
import type { QuizSocketMessage } from "./quizTypes";

/**
 * The race socket's quiz frames, applied to the `getQuiz` cache.
 *
 * - `quiz.guess`: someone locked a guess. It is appended to the cached feed
 *   (no scores while the quiz runs, so nothing else changes). When it is the
 *   viewer's own lock from another tab, or the cache holds no open board for
 *   that day yet, the board is refetched instead, so `you` stays right.
 * - `quiz.opened` (also a heist restart) and `quiz.closed` (the answer and
 *   the ranking only live on the board): refetch.
 *
 * The socket hook (`hooks/useAotdRaceSocket.ts`) is the only subscriber; it
 * also refetches the board on every reconnect.
 */

const QUIZ_TYPES = new Set(["quiz.opened", "quiz.guess", "quiz.closed"]);

export function isQuizSocketMessage(msg: unknown): msg is QuizSocketMessage {
  if (!msg || typeof msg !== "object") return false;
  const m = msg as { type?: unknown; dayKey?: unknown };
  return typeof m.type === "string" && QUIZ_TYPES.has(m.type) && typeof m.dayKey === "string";
}

type QuizThunk = ThunkAction<void, RootState, unknown, UnknownAction>;

export function refetchQuiz(): QuizThunk {
  return (dispatch) => {
    dispatch(artOfTheDayApi.util.invalidateTags(["AotdQuiz"]));
  };
}

export function applyQuizSocketMessage(msg: QuizSocketMessage): QuizThunk {
  return (dispatch, getState) => {
    if (msg.type !== "quiz.guess") {
      dispatch(refetchQuiz());
      return;
    }
    const guess = msg.guess;
    if (!guess || typeof guess.id !== "string" || !Array.isArray(guess.picks)) {
      dispatch(refetchQuiz());
      return;
    }

    const state = getState();
    const me = state.auth?.username ?? null;
    const cached = artOfTheDayApi.endpoints.getQuiz.select()(state).data?.quiz ?? null;

    if (me && guess.username === me) {
      dispatch(refetchQuiz());
      return;
    }
    if (!cached || cached.dayKey !== msg.dayKey || cached.status !== "open") {
      dispatch(refetchQuiz());
      return;
    }
    if (cached.guesses.some((g) => g.id === guess.id)) return;

    dispatch(
      artOfTheDayApi.util.updateQueryData("getQuiz", undefined, (draft) => {
        const quiz = draft.quiz;
        if (!quiz || quiz.dayKey !== msg.dayKey) return;
        if (quiz.guesses.some((g) => g.id === guess.id)) return;
        quiz.guesses.push({ ...guess, isYou: false });
        quiz.guesses.sort((a, b) => Date.parse(a.lockedAt) - Date.parse(b.lockedAt));
      }),
    );
  };
}
